import { ColumnVO } from "ag-grid-community/dist/lib/interfaces/iColumnVO";
import { ValueTypes } from "constants/product-collection";
import { List, Map } from "immutable";
import { IAllocationDimension } from "./allocation-dimension";
import {
    AggFuncTypes,
    FieldValueType,
    IDoorGroupMetric,
    IDoorGroupMetricName,
    IAttributeDoorsFilter,
} from "./server-rollup";
import { Metric } from "./metrics/types";
import { Attribute } from "reducers/dimensions-panel/dimensions-panel";
import { SortModelItem } from "ag-grid-community";

export enum OrderDirectionType {
    Asc = "asc",
    Desc = "desc",
}

export enum OperatorType {
    AND = "AND",
    OR = "OR",
}

export enum FilterType {
    NUMBER = "number",
    TEXT = "text",
    SET = "set",
}

export enum ColumnType {
    ItemField = "ITEM_FIELD",
    AllocationDimension = "ALLOCATION_DIMENSION",
    BulkFormulaField = "BULK_FORMULA_FIELD",
}

export enum FilterExpression {
    Equals = "equals",
    NotEqual = "notEqual",
    Contains = "contains",
    NotContains = "notContains",
    StartsWith = "startsWith",
    EndsWith = "endsWith",
    LessThan = "lessThan",
    LessThanOrEqual = "lessThanOrEqual",
    GreaterThan = "greaterThan",
    GreaterThanOrEqual = "greaterThanOrEqual",
    InRange = "inRange",
}

export const FilterExpressionHash = {
    [FilterExpression.Equals]: "eq",
    [FilterExpression.NotEqual]: "neq",
    [FilterExpression.Contains]: "ct",
    [FilterExpression.NotContains]: "nct",
    [FilterExpression.StartsWith]: "sw",
    [FilterExpression.EndsWith]: "ew",
    [FilterExpression.LessThan]: "lt",
    [FilterExpression.LessThanOrEqual]: "lte",
    [FilterExpression.GreaterThan]: "gt",
    [FilterExpression.GreaterThanOrEqual]: "gte",
    [FilterExpression.InRange]: "in",
};

export interface IOrderType {
    colId: string;
    sort: OrderDirectionType;
}

export interface IFilterField {
    filter: string | number | any;
    filterTo?: string | number | any;
    values?: string[];
    filterType: FilterType;
    type: FilterExpression;
}

export interface IComposeFilterField {
    condition1: IFilterField;
    condition2: IFilterField;
    filterType: FilterType;
    operator: OperatorType;
}

export interface IFilterType {
    [key: string]: IFilterField | IComposeFilterField;
}

export enum RollupTotalKind {
    DoorTotal = "DOOR_TOTAL",
    DoorMetricsTotal = "DOOR_METRICS_TOTAL",
    DoorTotalRow = "DOOR_TOTAL_ROW",
    FooterTotal = "FOOTER_TOTAL",
}

export interface IQueryDSLLookupParams {
    merchantId: string;
    dimensionFilters?: string[][];
    searchTokens?: string[];
    sortModel?: SortModelItem[];
    filterModel?: IFilterType;
    rowGroupCols?: ColumnVO[];
    pivotCols?: ColumnVO[];
    valueCols?: ColumnVO[];
    groupKeys?: string[];
    size: number;
    itemDisplayProperty: List<Map<string, any>>;
    calculateTotals?: boolean;
    calculateDoorMetrics?: boolean;
    totalKind?: RollupTotalKind;
    filterAssortmentBySMU?: string;
    searchAfter?: string[];
    offset?: number;
    dateDimensionId?: string;
    doorDimensionId?: string;
    sizeDimensionId?: string;
    merchantDateFormat?: string;
    productId?: string;
    doorId?: string;
    collectionId?: string;
    deliveryIds?: string[];
    doorIds?: string[];
    useAggSorting?: boolean;
    currentGroup?: ColumnVO;
    rollupAllocationDimensions?: IAllocationDimension[];
    doorMetrics?: Metric[];
    attributes?: Attribute[];
    doorGroupsDimensions?: string[][];
    assortmentTotals?: boolean;
    doorIdsWithinClassification?: string[];
    attributeDoorsFilters?: IAttributeDoorsFilter;
}

export interface IColumnRequest {
    id: string;
    display_name?: string;
    field: string;
    aggr_func?: AggFuncTypes;
    value_type?: ValueTypes;
}

interface ISortField {
    field: string;
    value_type?: ValueTypes;
    field_type: ColumnType;
}

interface ISortDoorField {
    door_id: string;
    delivery_id: string;
}

interface ISortAggregation {
    column_id: string;
    column?: IColumnRequest;
}

export interface ISortByRequest {
    order: "DESC" | "ASC";
    total_units?: ISortDoorField;
    field?: ISortField;
    aggregation?: ISortAggregation;
}

interface ISearchRequest {
    query: string;
    fields: string[];
    all_fields?: boolean;
}

interface IFilterOperator {
    field: string;
    value: FieldValueType;
    type: ColumnType;
}

interface IFilterInOperator {
    field: string;
    values: FieldValueType[];
    type: ColumnType;
}

interface IFilterCompose {
    filters: IFilterRequest[];
}
interface IFilterRequest {
    and?: IFilterCompose;
    or?: IFilterCompose;
    eq?: IFilterOperator;
    in?: IFilterInOperator;
    lt?: IFilterOperator;
    lte?: IFilterOperator;
    gt?: IFilterOperator;
    gte?: IFilterOperator;
    neq?: IFilterOperator;
    ct?: IFilterOperator;
    nct?: IFilterOperator;
    sw?: IFilterOperator;
    ew?: IFilterOperator;
}

interface IReviewRequest {
    by_product?: {
        product_id: string;
        collection_id: string;
    };
    by_door?: {
        door: string;
    };
    column_header: boolean;
    size_columns: true;
}

export interface IServerRollupRequest {
    merchant_id: string;
    page_size?: number;
    total?: boolean;
    door_total?: boolean;
    total_row?: boolean;
    delivery_dimension_id?: string;
    door_dimension_id?: string;
    size_dimension_id?: string;
    search_after?: string[]; // Needs to be provided for pagination without groups
    offset?: number; // Needs to be provided for pagination with grouping
    search?: ISearchRequest;
    delivery_filter?: string[];
    door_filter?: string[];
    filter?: IFilterRequest;
    group_by?: IColumnRequest[];
    pivot_by?: IColumnRequest[];
    value_columns?: IColumnRequest[];
    group_keys?: FieldValueType[];
    sort_by?: ISortByRequest[];
    review?: IReviewRequest;
    door_group_metrics?: IDoorGroupMetric[];
    door_groups?: IDoorGroupMetricName[];
    number_of_doors?: number;
    attribute_filter?: IDoorGroupMetricName[];
}
